import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext.jsx";
import { isSelfScroll } from "../utils/selfScroll.js";

export default function CartOverlay() {
  const { isOverlayOpen, closeOverlay, lastAddedItem, totalItems } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const isCartPage = location.pathname === "/featured/cart" || location.pathname === "/cart";
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const closingTimer = useRef(null);

  const handleClose = useCallback(() => {
    if (closing) return;
    setClosing(true);
    closingTimer.current = setTimeout(() => {
      setClosing(false);
      setVisible(false);
      closeOverlay();
    }, 150);
  }, [closing, closeOverlay]);

  useEffect(() => {
    if (isOverlayOpen && lastAddedItem) {
      if (isCartPage) {
        closeOverlay();
        return;
      }
      setVisible(true);
      setClosing(false);
    }
  }, [isOverlayOpen, lastAddedItem, isCartPage, closeOverlay]);

  useEffect(() => {
    if (isOverlayOpen) {
      const timer = setTimeout(() => {
        handleClose();
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isOverlayOpen, handleClose]);

  useEffect(() => {
    if (!isOverlayOpen) return;
    // The visitor scrolling closes it; a row scrolling itself (the home
    // page's NEW ARRIVALS, utils/selfScroll) does not, or the overlay would
    // close the moment it opened.
    const handleScroll = (e) => { if (!isSelfScroll(e)) handleClose(); };
    const opts = { passive: true, capture: true };
    window.addEventListener("scroll", handleScroll, opts);
    return () => window.removeEventListener("scroll", handleScroll, opts);
  }, [isOverlayOpen, handleClose]);

  useEffect(() => {
    return () => { if (closingTimer.current) clearTimeout(closingTimer.current); };
  }, []);

  if (!visible || !lastAddedItem) {
    return null;
  }

  const item = lastAddedItem.item || lastAddedItem;
  const imageUrl = item.photos_json?.[item.cover_index]?.url || item.photos_json?.[0]?.url || item.image_url || null;

  return createPortal(
    <div className="cart-overlay" role="dialog" aria-modal="true" aria-labelledby="cart-overlay-title">
      <div className={`cart-overlay__backdrop${closing ? " cart-overlay__backdrop--closing" : ""}`} onClick={handleClose} aria-hidden="true" />
      <div className={`cart-overlay__content${closing ? " cart-overlay__content--closing" : ""}`}>
        <div className="cart-overlay__header">
          <h2 id="cart-overlay-title" className="cart-overlay__title">ADDED TO CART</h2>
          <button
            type="button"
            className="cart-overlay__close"
            onClick={handleClose}
            aria-label="Close cart overlay"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="square" strokeLinejoin="miter">
              <line x1="5" y1="5" x2="19" y2="19" />
              <line x1="19" y1="5" x2="5" y2="19" />
            </svg>
          </button>
        </div>
        <div className="cart-overlay__item">
          {imageUrl && (
            <img
              src={imageUrl}
              alt={item.title || "Item"}
              className="cart-overlay__item-image"
            />
          )}
          <div className="cart-overlay__item-details">
            <div className="cart-overlay__item-name">{item.title || "Item"}</div>
            {(lastAddedItem.size || lastAddedItem.color) && (
              <div className="cart-overlay__item-meta">
                {lastAddedItem.size && <span>Size: {lastAddedItem.size}</span>}
                {lastAddedItem.size && lastAddedItem.color && " / "}
                {lastAddedItem.color && <span>Color: {lastAddedItem.color}</span>}
              </div>
            )}
            <div className="cart-overlay__item-qty">Qty: {lastAddedItem.quantity || 1}</div>
          </div>
        </div>
        <div className="cart-overlay__actions">
          <button
            type="button"
            className="cart-overlay__button cart-overlay__button--primary"
            onClick={() => {
              handleClose();
              navigate("/featured/cart");
            }}
          >
            VIEW CART ({totalItems})
          </button>
          <button
            type="button"
            className="cart-overlay__button cart-overlay__button--secondary"
            onClick={handleClose}
          >
            Continue shopping
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
